/**
 * Identity API Client
 *
 * API client for managing identity operations (users, tenants) in the MATIC system.
 * Handles user management, tenant operations, and identity-related queries.
 */

import {AuthenticatedApiClient} from '@matic/lib/api/authenticated-api-client';
import {isValidUuid} from '@matic/lib/utils';
import type {
  User,
  UserList,
  Tenant,
  TenantResponse,
  MyProfile,
  MyProfileResponse,
  UpdateMyProfileRequest,
} from './types';
import {type UserStatus, type UserRole} from '@/matic/types/roles';

// ============================================================================
// API Response Types
// ============================================================================

/**
 * User response from identity API
 */
export type UserResponse = {
  id: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  tenant_id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  created_at: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  updated_at: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  created_by?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  job_title?: string | null;
  department?: string | null;
};

/**
 * Distinct user attribute values response — backs the typeahead
 * combobox on the user form and the matrix attribute filters.
 */
export type DistinctUserAttributeValuesResponse = {
  items: string[];
};

/**
 * User list response from identity API
 */
export type UserListResponse = {
  items: UserResponse[];
  total: number;
  limit: number;
  offset: number;
};

/**
 * Query parameters for user list
 */
export type UserListParameters = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  tenant_id: string; // TODO: likely remove this
  limit?: number;
  offset?: number;
  role?: string;
  status?: string;
};

/**
 * Request data for creating a user
 */
export type CreateUserRequest = {
  email: string;
  name: string;
  role: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  job_title?: string | null;
  department?: string | null;
};

/**
 * Request data for updating a user
 */
export type UpdateUserRequest = {
  name?: string;
  role?: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  job_title?: string | null;
  department?: string | null;
};

// ============================================================================
// Identity API Client
// ============================================================================

export class IdentityApiClient extends AuthenticatedApiClient {
  private get basePath() {
    return '/api/v1/identity';
  }

  /**
   * Get a list of users for the current tenant (from session)
   *
   * This method retrieves users without requiring an explicit tenantId parameter.
   * The backend extracts the tenant from the authenticated JWT session.
   *
   * This is the recommended method for fetching users. Use this instead of getUsers().
   *
   * @param options - Query options
   * @returns Promise<UserList>
   *
   * @example
   * ```typescript
   * // Fetch all users for current tenant
   * const users = await identityApi.getUsersFromSession();
   *
   * // Fetch with filters
   * const admins = await identityApi.getUsersFromSession({ role: 'admin' });
   * ```
   */
  async getUsersFromSession(
    options: {
      limit?: number;
      offset?: number;
      role?: string;
      status?: string;
    } = {}
  ): Promise<UserList> {
    const queryParameters = new URLSearchParams();

    if (options.limit !== undefined) {
      queryParameters.append('limit', options.limit.toString());
    }

    if (options.offset !== undefined) {
      queryParameters.append('offset', options.offset.toString());
    }

    if (options.role) {
      queryParameters.append('role', options.role);
    }

    if (options.status) {
      queryParameters.append('status', options.status);
    }

    const endpoint = `${this.basePath}/users${queryParameters.toString() ? `?${queryParameters.toString()}` : ''}`;

    try {
      const apiResponse = await this.get<UserListResponse>(endpoint);
      return this.convertUserListResponse(apiResponse);
    } catch (error) {
      throw this.handleIdentityError(error, endpoint);
    }
  }

  /**
   * Get a specific user by ID
   *
   * Tenant ID is automatically extracted from the authenticated JWT token.
   *
   * @param userId - User ID (UUID)
   * @returns Promise<User>
   */
  async getUser(userId: string): Promise<User> {
    if (!isValidUuid(userId)) {
      throw new Error('User ID must be a valid UUID');
    }

    const endpoint = `${this.basePath}/users/${userId}`;

    try {
      const apiResponse = await this.get<UserResponse>(endpoint);
      return this.convertUserResponse(apiResponse);
    } catch (error) {
      throw this.handleIdentityError(error, endpoint);
    }
  }

  /**
   * Get the signed-in user's own profile.
   *
   * Scoped server-side to the JWT's user — there is no user ID parameter.
   * Returns `workFocus`, which `getUser` deliberately does not expose.
   *
   * @returns Promise<MyProfile>
   */
  async getMyProfile(): Promise<MyProfile> {
    const endpoint = `${this.basePath}/users/me/profile`;

    try {
      const apiResponse = await this.get<MyProfileResponse>(endpoint);
      return this.convertMyProfileResponse(apiResponse);
    } catch (error) {
      throw this.handleIdentityError(error, endpoint);
    }
  }

  /**
   * Update the signed-in user's own profile.
   *
   * Open to every role — this is the self-service counterpart to `updateUser`,
   * which is the admin-facing path. Omit a field to leave it unchanged; pass
   * `null` to clear it.
   *
   * @param profileData - Fields to change
   * @returns Promise<MyProfile>
   */
  async updateMyProfile(
    profileData: UpdateMyProfileRequest
  ): Promise<MyProfile> {
    const endpoint = `${this.basePath}/users/me/profile`;

    try {
      const apiResponse = await this.put<MyProfileResponse>(
        endpoint,
        profileData
      );
      return this.convertMyProfileResponse(apiResponse);
    } catch (error) {
      throw this.handleIdentityError(error, endpoint);
    }
  }

  /**
   * Create a new user
   *
   * Tenant ID is automatically extracted from the authenticated JWT token.
   *
   * @param userData - User creation data
   * @param createdBy - ID of user creating this user (optional, will use current session user if not provided)
   * @returns Promise<User>
   */
  async createUser(
    userData: CreateUserRequest,
    createdBy?: string
  ): Promise<User> {
    const endpoint = `${this.basePath}/users`;

    // Add created_by as query parameter if provided
    const queryParameters = createdBy
      ? `?created_by=${encodeURIComponent(createdBy)}`
      : '';
    const fullEndpoint = `${endpoint}${queryParameters}`;

    try {
      const apiResponse = await this.post<UserResponse>(fullEndpoint, userData);
      return this.convertUserResponse(apiResponse);
    } catch (error) {
      throw this.handleIdentityError(error, endpoint);
    }
  }

  /**
   * Update an existing user
   *
   * Tenant ID is automatically extracted from the authenticated JWT token.
   *
   * @param userId - User ID (UUID)
   * @param userData - User update data
   * @returns Promise<User>
   */
  async updateUser(userId: string, userData: UpdateUserRequest): Promise<User> {
    if (!isValidUuid(userId)) {
      throw new Error('User ID must be a valid UUID');
    }

    const endpoint = `${this.basePath}/users/${userId}`;

    try {
      const apiResponse = await this.put<UserResponse>(endpoint, userData);
      return this.convertUserResponse(apiResponse);
    } catch (error) {
      throw this.handleIdentityError(error, endpoint);
    }
  }

  /**
   * Delete a user
   *
   * Tenant ID is automatically extracted from the authenticated JWT token.
   *
   * @param userId - User ID (UUID)
   * @returns Promise<void>
   */
  async deleteUser(userId: string): Promise<void> {
    if (!isValidUuid(userId)) {
      throw new Error('User ID must be a valid UUID');
    }

    const endpoint = `${this.basePath}/users/${userId}`;

    try {
      await this.delete<void>(endpoint);
    } catch (error) {
      throw this.handleIdentityError(error, endpoint);
    }
  }

  /**
   * Activate a user
   *
   * Tenant ID is automatically extracted from the authenticated JWT token.
   *
   * @param userId - User ID (UUID)
   * @returns Promise<User>
   */
  async activateUser(userId: string): Promise<User> {
    if (!isValidUuid(userId)) {
      throw new Error('User ID must be a valid UUID');
    }

    const endpoint = `${this.basePath}/users/${userId}/activate`;

    try {
      const apiResponse = await this.post<UserResponse>(endpoint, {});
      return this.convertUserResponse(apiResponse);
    } catch (error) {
      throw this.handleIdentityError(error, endpoint);
    }
  }

  /**
   * Deactivate a user
   *
   * Tenant ID is automatically extracted from the authenticated JWT token.
   *
   * @param userId - User ID (UUID)
   * @returns Promise<User>
   */
  async deactivateUser(userId: string): Promise<User> {
    if (!isValidUuid(userId)) {
      throw new Error('User ID must be a valid UUID');
    }

    const endpoint = `${this.basePath}/users/${userId}/deactivate`;

    try {
      const apiResponse = await this.post<UserResponse>(endpoint, {});
      return this.convertUserResponse(apiResponse);
    } catch (error) {
      throw this.handleIdentityError(error, endpoint);
    }
  }

  /**
   * List distinct job titles in the current tenant.
   *
   * Backs the typeahead combobox on the user form and the matrix's
   * job-title filter picker.
   */
  async listJobTitles(limit = 500): Promise<string[]> {
    const endpoint = `${this.basePath}/users/job-titles?limit=${limit}`;
    try {
      const apiResponse =
        await this.get<DistinctUserAttributeValuesResponse>(endpoint);
      return apiResponse.items;
    } catch (error) {
      throw this.handleIdentityError(error, endpoint);
    }
  }

  /**
   * List distinct departments in the current tenant.
   *
   * Backs the typeahead combobox on the user form and the matrix's
   * department filter picker.
   */
  async listDepartments(limit = 500): Promise<string[]> {
    const endpoint = `${this.basePath}/users/departments?limit=${limit}`;
    try {
      const apiResponse =
        await this.get<DistinctUserAttributeValuesResponse>(endpoint);
      return apiResponse.items;
    } catch (error) {
      throw this.handleIdentityError(error, endpoint);
    }
  }

  // ========================================
  // Tenant Operations
  // ========================================

  /**
   * Get a tenant by ID
   *
   * @param tenantId - Tenant ID (UUID)
   * @returns Promise<Tenant> - Tenant details
   *
   * @example
   * ```typescript
   * const tenant = await identityApi.getTenant('123e4567-e89b-12d3-a456-426614174000');
   * console.log(tenant.name); // "Acme Corporation"
   * ```
   */
  async getTenant(tenantId: string): Promise<Tenant> {
    const endpoint = `${this.basePath}/tenants/${tenantId}`;

    try {
      const apiResponse = await this.get<TenantResponse>(endpoint);
      return this.convertTenantResponse(apiResponse);
    } catch (error) {
      throw this.handleIdentityError(error, endpoint);
    }
  }

  /**
   * Convert API UserResponse to frontend User type
   */
  private convertUserResponse(apiUser: UserResponse): User {
    return {
      id: apiUser.id,
      tenantId: apiUser.tenant_id,
      email: apiUser.email,
      name: apiUser.name,
      role: apiUser.role as UserRole, // Cast API string to UserRole type
      status: apiUser.status as UserStatus,
      createdAt: apiUser.created_at,
      updatedAt: apiUser.updated_at,
      createdBy: apiUser.created_by,
      jobTitle: apiUser.job_title ?? undefined,
      department: apiUser.department ?? undefined,
    };
  }

  /**
   * Convert API MyProfileResponse to the frontend MyProfile type
   */
  private convertMyProfileResponse(apiProfile: MyProfileResponse): MyProfile {
    return {
      userId: apiProfile.user_id,
      email: apiProfile.email,
      name: apiProfile.name,
      role: apiProfile.role as UserRole,
      jobTitle: apiProfile.job_title ?? undefined,
      department: apiProfile.department ?? undefined,
      workFocus: apiProfile.work_focus ?? undefined,
    };
  }

  /**
   * Convert API UserListResponse to frontend UserList type
   */
  private convertUserListResponse(apiResponse: UserListResponse): UserList {
    return {
      items: apiResponse.items.map((user) => this.convertUserResponse(user)),
      total: apiResponse.total,
    };
  }

  /**
   * Convert API TenantResponse to frontend Tenant type
   */
  private convertTenantResponse(apiTenant: TenantResponse): Tenant {
    return {
      id: apiTenant.id,
      name: apiTenant.name,
      mfaRequired: apiTenant.mfa_required,
      cuiEnabled: apiTenant.cui_enabled,
      qmsRecordsEnabled: apiTenant.qms_records_enabled,
      createdAt: apiTenant.created_at,
      updatedAt: apiTenant.updated_at,
      createdBy: apiTenant.created_by,
    };
  }

  // ========================================
  // Error Handling
  // ========================================

  /**
   * Handle identity-specific errors
   */
  private handleIdentityError(error: unknown, endpoint: string): Error {
    if (error instanceof Error) {
      // Add context to the error
      const contextualError = new Error(
        `Identity API Error (${endpoint}): ${error.message}`
      );
      contextualError.stack = error.stack;
      return contextualError;
    }

    return new Error(
      `Identity API Error (${endpoint}): Unknown error occurred`
    );
  }
}
// ============================================================================
// Factory Function (Recommended Pattern)
// ============================================================================

/**
 * Cached singleton factory for IdentityApiClient
 *
 * Returns a cached instance per token to avoid unnecessary recreation.
 * This provides singleton efficiency while maintaining proper OIDC integration.
 *
 * @param token - Optional OIDC token from useApiToken()
 * @returns Cached IdentityApiClient instance
 *
 * @example
 * ```typescript
 * const token = useApiToken();
 * const api = getIdentityApi(token); // Cached by token
 * const users = await api.getUsersFromSession();
 * ```
 */
const clientCache = new Map<string, IdentityApiClient>();

export function getIdentityApi(token?: string): IdentityApiClient {
  const key = token ?? 'no-token';

  if (!clientCache.has(key)) {
    clientCache.set(key, new IdentityApiClient(token));
  }

  return clientCache.get(key)!;
}

// Legacy singleton - deprecated, use getIdentityApi(token) instead
export const identityApi = new IdentityApiClient();
export default identityApi;
